import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useEffectOnce } from 'react-use';
import doors from '~/assets/icons/doors.svg';
import engine from '~/assets/icons/engine.svg';
import gaz from '~/assets/icons/gaz.svg';
import lights from '~/assets/icons/lights.svg';
import seatbelt from '~/assets/icons/seatbelt.svg';
import { rage, useObservable } from '~/rage';
import { HandlingMode, VehicleType } from '~/shared';
import turnSignal from '../../assets/icons/turn_signal.png';
import { useSyncAnimations } from '../../hooks';
import { IconStroke } from '../IconStroke';
import { TextLabelStroke } from '../TextLabelStroke';
import {
	SpeedometerContainer,
	StyledBackgroundBig,
	StyledBackgroundSmall,
	StyledContainer,
	StyledEngineButton,
	StyledEngineButtonBorder,
	StyledGazCircle,
	StyledGazContainer,
	StyledGazLabels,
	StyledHelpInfoContainer,
	StyledInfoBottomContainer,
	StyledInfoCarContainer,
	StyledNotActiveBackground,
	StyledSpeedInfoContainer,
	StyledSpeedText,
	StyledStatusIcon,
	StyledTurnSignal,
} from './Speedometer.styled';

const handlingModes = {
	[HandlingMode.Comfort]: {
		name: 'COMFORT',
		color: '#1782FF',
	},
	[HandlingMode.Drift]: {
		name: 'DRIFT',
		color: '#FABE2C',
	},
	[HandlingMode.Sport]: {
		name: 'SPORT',
		color: '#EB1818',
	},
} as const;

type HandlingPresentation = (typeof handlingModes)[keyof typeof handlingModes];

function resolveHandlingPresentation(mode: HandlingMode): HandlingPresentation {
	const preset = handlingModes[mode];
	return preset ?? handlingModes[HandlingMode.Comfort];
}

const Speedometer: React.FC = () => {
	const maxSpeed = useObservable(rage.vehicles.getLocalVehicleMaxSpeed());
	const fuelCapacity = useObservable(
		rage.vehicles.getLocalVehicleFuelTankCapacity(),
	);
	const active = useObservable(rage.vehicles.getEngineState());
	const mode = useObservable(rage.vehicles.getLocalVehicleHandlingMode());
	const vehicleType = useObservable(rage.vehicles.getLocalVehicleVehicleType());
	const lockState = useObservable(rage.vehicles.getLocalVehicleLockState());
	const lightsState = useObservable(rage.vehicles.getLocalVehicleLightsState());
	const seatbeltState = useObservable(rage.vehicles.getSeatbeltState());
	const turnState = useObservable(
		rage.vehicles.getLocalVehicleTurnLightsState(),
	);
	const [turnLeftState, setTurnLeftState] = useState<boolean>(false);
	const [turnRightState, setTurnRightState] = useState<boolean>(false);
	const { leftRefState, rightRefState } = useSyncAnimations(
		turnLeftState,
		turnRightState,
	);
	const speedHundredsRef = useRef<HTMLSpanElement>(null); // сотни
	const speedTensRef = useRef<HTMLSpanElement>(null); // десятки
	const speedUnitsRef = useRef<HTMLSpanElement>(null); // единицы
	const speedCircleRef = useRef<SVGCircleElement>(null);
	const fuelValueRef = useRef<number>(
		rage.vehicles.getLocalVehicleFuel().getValue(),
	);
	const fuelCircleRef = useRef<SVGCircleElement>(null);
	const mileageValueRef = useRef<HTMLLabelElement>(null);

	const handlingMode = useMemo(() => {
		const modeForUi =
			vehicleType === VehicleType.BIKE ? HandlingMode.Comfort : mode;
		return resolveHandlingPresentation(modeForUi);
	}, [vehicleType, mode]);

	useEffectOnce(() => {
		const speedSub = rage.vehicles.getLocalVehicleSpeed().subscribe((value) => {
			if (
				speedHundredsRef.current &&
				speedTensRef.current &&
				speedUnitsRef.current
			) {
				const roundedSpeed = Math.floor(value);
				speedHundredsRef.current.textContent = String(
					Math.floor((roundedSpeed / 100) % 10),
				);
				speedTensRef.current.textContent = String(
					Math.floor((roundedSpeed / 10) % 10),
				);
				speedUnitsRef.current.textContent = String(roundedSpeed % 10);

				speedHundredsRef.current.style.opacity =
					roundedSpeed < 100 ? '0.25' : '1';
				speedTensRef.current.style.opacity = roundedSpeed < 10 ? '0.25' : '1';
				speedUnitsRef.current.style.opacity = roundedSpeed === 0 ? '0.25' : '1';

				if (speedCircleRef.current) {
					speedCircleRef.current.setAttribute(
						'stroke-dashoffset',
						String(
							622 -
								((roundedSpeed / maxSpeed) * 100 <= 100
									? (roundedSpeed / maxSpeed) * 100
									: 100) *
									4.67,
						),
					);
				}
			}
		});

		const fuelSub = rage.vehicles.getLocalVehicleFuel().subscribe((value) => {
			fuelValueRef.current = value;
			if (fuelCircleRef.current) {
				fuelCircleRef.current.setAttribute(
					'stroke-dashoffset',
					String(
						935.72 -
							(Math.round((value / fuelCapacity) * 100) <= 100
								? Math.round((value / fuelCapacity) * 100)
								: 100) *
								1.57,
					),
				);

				fuelCircleRef.current.style.stroke =
					fuelValueRef.current > 20 ? '#FFFFFF' : '#EB1818';
			}
		});

		const mileageSub = rage.vehicles
			.getLocalVehicleMileage()
			.subscribe((value) => {
				if (mileageValueRef.current) {
					mileageValueRef.current.textContent = `${value.toLocaleString(
						'ru-RU',
					)} км.`;
				}
			});

		return () => {
			speedSub.unsubscribe();
			fuelSub.unsubscribe();
			mileageSub.unsubscribe();
		};
	});

	useEffect(() => {
		setTurnLeftState(turnState.left);
		setTurnRightState(turnState.right);
	}, [turnState]);

	return (
		<StyledContainer>
			<StyledBackgroundBig style={{ zIndex: active ? 1 : 15 }} />
			<StyledBackgroundSmall style={{ zIndex: active ? 2 : 15 }} />
			<CSSTransition
				in={!active}
				classNames="background"
				timeout={500}
				unmountOnExit
				appear={true}
			>
				<StyledNotActiveBackground />
			</CSSTransition>
			<CSSTransition
				in={!active}
				classNames="background"
				timeout={500}
				unmountOnExit
				appear={true}
			>
				<StyledNotActiveBackground />
			</CSSTransition>
			<SpeedometerContainer>
				<svg width={240} height={240}>
					<clipPath id="cut-circle-1">
						<rect x="0" y="0" width="240" height="205" />
					</clipPath>
					<circle
						cx={120}
						cy={120}
						r={118}
						strokeWidth={1}
						stroke={'white'}
						fill="none"
						clipPath="url(#cut-circle-1)"
						opacity={0.1}
					></circle>
					<clipPath id="cut-circle-2">
						<rect x="19" y="20" width="200" height="171" />
					</clipPath>
					<circle
						cx={119}
						cy={120}
						r={99}
						strokeWidth={1}
						stroke={'white'}
						fill="none"
						strokeDasharray="1, 19"
						strokeDashoffset="1%"
						clipPath='url("#cut-circle-2")'
					></circle>
					<clipPath id="cut-circle-3">
						<rect x="40" y="40" width="160" height="137" />
					</clipPath>
					<circle
						cx={120}
						cy={120}
						r={79}
						strokeWidth={1}
						stroke={'white'}
						fill="none"
						clipPath="url(#cut-circle-3)"
						opacity={0.15}
					></circle>
					<clipPath id="cut-circle-4">
						<rect x="60" y="60" width="120" height="103" />
					</clipPath>
					<circle
						cx={120}
						cy={120}
						r={59}
						strokeWidth={1}
						stroke={'white'}
						fill="none"
						clipPath="url(#cut-circle-4)"
						opacity={0.1}
					></circle>
					<clipPath id="cut-circle-5">
						<rect x="80" y="80" width="80" height="69" />
					</clipPath>
					<circle
						cx={120}
						cy={120}
						r={39}
						strokeWidth={1}
						stroke={'white'}
						fill="none"
						clipPath="url(#cut-circle-5)"
						opacity={0.05}
					></circle>
					<clipPath id="cut-circle-speed">
						<rect x="17" y="17" width="206" height="171" />
					</clipPath>
					<circle
						cx={120}
						cy={120}
						r={99}
						strokeWidth={9}
						stroke={handlingMode.color}
						fill="none"
						strokeDasharray={622}
						strokeLinecap="round"
						ref={speedCircleRef}
						style={{ rotate: '135deg', transform: 'translate(-50%, -120%)' }}
					></circle>
				</svg>
			</SpeedometerContainer>
			<StyledSpeedInfoContainer>
				<TextLabelStroke
					style={{
						color: handlingMode.color,
						fontSize: '12px',
						fontWeight: 700,
						visibility: vehicleType === VehicleType.BIKE ? 'hidden' : 'visible',
						opacity: active ? 1 : 0,
						fontStyle: 'italic',
						transition: 'color 0.3s ease',
					}}
				>
					{handlingMode.name}
				</TextLabelStroke>
				<TextLabelStroke
					style={{
						marginTop: '10px',
						fontSize: '14px',
						fontWeight: 300,
						fontStyle: 'italic',
					}}
					ref={mileageValueRef}
				>
					{/* {mileageValueRef.current?.toLocaleString('ru-RU')} км. */}
				</TextLabelStroke>
				<StyledSpeedText>
					<span ref={speedHundredsRef}>0</span>
					<span ref={speedTensRef}>0</span>
					<span ref={speedUnitsRef}>0</span>
				</StyledSpeedText>
				<StyledInfoBottomContainer>
					<StyledTurnSignal
						src={turnSignal}
						$active={turnLeftState}
						ref={leftRefState}
						style={{
							transform: 'scale(-1, -1)',
						}}
					/>
					<TextLabelStroke
						style={{
							fontSize: '12px',
							fontWeight: 300,
							color: 'rgba(255, 255, 255, 0.75)',
							fontStyle: 'italic',
						}}
					>
						КМ/Ч
					</TextLabelStroke>
					<StyledTurnSignal
						src={turnSignal}
						$active={turnRightState}
						ref={rightRefState}
					/>
				</StyledInfoBottomContainer>
			</StyledSpeedInfoContainer>
			<CSSTransition
				in={!active}
				classNames="helpinfo"
				timeout={1000}
				unmountOnExit
				appear={true}
			>
				<StyledHelpInfoContainer>
					<TextLabelStroke
						style={{ fontSize: '14px', fontWeight: 500, textAlign: 'center' }}
					>
						Чтобы запустить двигатель
						<br />
						нажмите на кнопку
					</TextLabelStroke>
					<StyledEngineButton>
						2
						<StyledEngineButtonBorder />
					</StyledEngineButton>
				</StyledHelpInfoContainer>
			</CSSTransition>
			<StyledInfoCarContainer>
				<StyledStatusIcon src={doors} $active={lockState} />
				<StyledStatusIcon src={lights} $active={lightsState} />
				<StyledStatusIcon src={seatbelt} $active={seatbeltState} />
				<StyledStatusIcon src={engine} $active={active} />
			</StyledInfoCarContainer>
			<StyledGazContainer>
				<svg width={300} height={290}>
					<clipPath id="cut-circle-gaz">
						<rect x="10" y="0" width="22" height="290" />
					</clipPath>
					<circle
						cx={155}
						cy={140}
						r={144}
						strokeWidth={2}
						stroke={'white'}
						fill="none"
						strokeDasharray="1, 10"
						strokeDashoffset="3.5%"
						clipPath='url("#cut-circle-gaz")'
					></circle>
					<StyledGazCircle
						cx={160}
						cy={140}
						r={149}
						strokeWidth={6}
						fill="none"
						strokeDasharray={935.72}
						ref={fuelCircleRef}
						strokeLinecap="round"
					></StyledGazCircle>
				</svg>
				<StyledGazLabels>
					<TextLabelStroke
						style={{ fontSize: '14px', fontWeight: 400, opacity: 0.5 }}
					>
						F
					</TextLabelStroke>
					<IconStroke src={gaz} style={{ paddingRight: '8px' }} opacity={0.5} />
					<TextLabelStroke
						style={{ fontSize: '14px', fontWeight: 400, opacity: 0.5 }}
					>
						E
					</TextLabelStroke>
				</StyledGazLabels>
			</StyledGazContainer>
		</StyledContainer>
	);
};

export { Speedometer };
